import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as authApi from '@/api/auth'
import { clearAccessToken, getAccessToken } from '@/api/client'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [siteRequireMfa, setSiteRequireMfa] = useState(true)

  const loadUser = useCallback(async () => {
    const data = await authApi.fetchCurrentUser()
    setUser(data)
    return data
  }, [])

  const bootstrap = useCallback(async () => {
    setLoading(true)
    try {
      try {
        const res = await fetch('/api/health')
        if (res.ok) {
          const health = await res.json()
          if (typeof health.require_mfa === 'boolean') {
            setSiteRequireMfa(health.require_mfa)
          }
        }
      } catch {
        // Keep default if health is unreachable
      }

      const token = getAccessToken()
      if (token) {
        await loadUser()
      } else {
        try {
          await authApi.refreshSession()
          await loadUser()
        } catch {
          setUser(null)
        }
      }
    } catch {
      clearAccessToken()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [loadUser])

  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  useEffect(() => {
    const onUnauthorized = () => {
      clearAccessToken()
      setUser(null)
      setLoading(false)
    }
    window.addEventListener('atera:unauthorized', onUnauthorized)
    return () => window.removeEventListener('atera:unauthorized', onUnauthorized)
  }, [])

  const login = useCallback(
    async (email, password, totpCode, rememberMfa = false) => {
      await authApi.login(email, password, totpCode, rememberMfa)
      return loadUser()
    },
    [loadUser],
  )

  const logout = useCallback(async () => {
    await authApi.logout()
    clearAccessToken()
    setUser(null)
  }, [])

  const requireMfa =
    user && typeof user.require_mfa === 'boolean' ? user.require_mfa : siteRequireMfa

  const customerIds = Array.isArray(user?.customer_ids) ? user.customer_ids : []
  const canFilterCustomers =
    user?.role === 'admin' ||
    user?.role === 'staff' ||
    (user?.role === 'customer' && customerIds.length > 1)

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      refreshUser: loadUser,
      setUser,
      isAdmin: user?.role === 'admin',
      isStaff: user?.role === 'admin' || user?.role === 'staff',
      isCustomer: user?.role === 'customer',
      customerIds,
      canFilterCustomers,
      mfaEnabled: Boolean(user?.totp_enabled),
      requireMfa,
    }),
    [user, loading, login, logout, loadUser, requireMfa, customerIds, canFilterCustomers],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
